/**
 * Canonical brand-foreground contrast (CON-5, made WCAG-correct by CON-10).
 *
 * This is the ONE implementation of the donor-side "which ink goes on the
 * nonprofit's colour" decision. `packages/shared/src/styling/ColorSwap.js`
 * re-exports `contrastForeground` as `setContrast`, so the fleet has one copy
 * of this behaviour and not an eighth fork.
 *
 * ── What CON-10 changed, and why ──────────────────────────────────────────────
 * M1 moved this function without touching it: it picked its ink by comparing a
 * RAW-RGB weighted sum against a fixed threshold, which is not a contrast ratio
 * and is not WCAG. `design.md` §4.3 measured what that costs across the nine
 * shipped default brand hexes plus three test hues: on three of them the
 * heuristic picked the ink with the WORSE real contrast, and on four the better
 * of its two inks was still under the 4.50:1 floor `design.md` §1.4 sets for
 * body text. The sage the product ships when an organisation has set no colour
 * was the worst case in the set.
 *
 * D7 makes the email deriver's machinery canonical
 * (`backends/messaging/src/emails/components/theme.ts`, pinned by
 * `__tests__/emails/theme-contrast.test.ts`): WCAG relative luminance, a real
 * contrast ratio, and — the part that turns "better" into "guaranteed" — a
 * fallback to the extreme ink when neither of the house inks clears the floor.
 * Per D-email-hardcodes the two sides stay two copies rather than an import.
 *
 * **They adopt the same MACHINERY; they do not return the same values, and
 * nothing here should be read as claiming they do.** Four differences, each
 * deliberate and each measured (CON-10 record §5): the email deriver walks
 * toward its own paper ink and this one toward the donor set's; it starts from
 * the brand itself where this starts from whichever ramp step the call site
 * asks about; it checks two grounds where this checks the one it is given; and
 * its label escalates only to black where this picks the better extreme, so the
 * two disagree on a mid-luminance hue where white is the better choice. Making
 * them agree on VALUES is CON-12's half of the work, and the fixture table is
 * where that gets checked — this file cannot check it, because a web test may
 * not import from `backends/`.
 *
 * ── The guarantee, which is a proof and not a sample ──────────────────────────
 * Against a background of relative luminance L, black scores `20L + 1` and
 * white scores `1.05 / (L + 0.05)`. The two are equal at L = 0.17913, where both
 * score 4.5826:1, and each rises monotonically away from that point. So
 * `max(black, white)` has a global minimum of 4.5826:1 over EVERY colour that
 * can be expressed — above the 4.50:1 floor with no case analysis. The
 * guarantee is only the guarantee if the escalation picks the better EXTREME on
 * its own ratio; inheriting the house inks' direction breaks it on a narrow
 * luminance band, which is a mistake this file made once and the sweep caught.
 * That is why {@link contrastForeground} can promise AA on any input rather
 * than promising to try, and it is why moving the brand ramp's own lightness is not needed to
 * make the call-to-action label readable (see the CON-10 record for the
 * `design.md` §1.5 / §6.10 correction that follows from this).
 *
 * Zero dependencies — this file must stay importable from the values-only
 * entrypoint of @charitystack/design-tokens.
 */

/** House ink for a light brand background. Preferred first: it is the donor set's own text colour. */
export const CONTRAST_DARK_TEXT = 'rgb(51, 51, 51)';
/** House ink for a dark brand background. */
export const CONTRAST_LIGHT_TEXT = 'rgb(244, 243, 242)';
/**
 * The extremes, used ONLY when neither house ink clears {@link CONTRAST_AA_NORMAL}.
 * They are what makes the floor a guarantee rather than a best effort; the house
 * inks are tried first so a hue that never needed the escalation does not get it.
 */
export const CONTRAST_DARK_TEXT_MAX = '#000000';
export const CONTRAST_LIGHT_TEXT_MAX = '#FFFFFF';
/** Returned when the input cannot be parsed at all. */
export const CONTRAST_ERROR_FALLBACK = '#FFFFFF';

/** WCAG 2.1 SC 1.4.3 minimum for normal-size body text — `design.md` §1.4's floor. */
export const CONTRAST_AA_NORMAL = 4.5;

/** WCAG 2.1 SC 1.4.3 / 1.4.11 minimum for large text and UI components — the floor a brand fill's label clears. */
export const CONTRAST_AA_LARGE = 3;

/**
 * How {@link brandTextForeground} walks a brand step toward readability: the
 * email deriver's own step size and iteration cap (`accessibleForeground`), so
 * the two copies converge on the same answer for the same input.
 */
const BRAND_TEXT_STEP = 0.12;
const BRAND_TEXT_MAX_STEPS = 16;

function parseChannels(hex) {
  // Validate input
  if (!hex || typeof hex !== 'string') {
    throw new Error(`Invalid hex color format: expected string, got ${typeof hex}`);
  }

  // Clean the hex string
  const cleanHex = hex.replace(/^#/, '').trim();

  // Validate hex format
  if (!/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(cleanHex)) {
    throw new Error(`Invalid hex color format: "${hex}" (expected 3 or 6 hex characters)`);
  }

  const full =
    cleanHex.length === 3
      ? cleanHex
          .split('')
          .map((channel) => channel + channel)
          .join('')
      : cleanHex;

  return [
    parseInt(full.substring(0, 2), 16),
    parseInt(full.substring(2, 4), 16),
    parseInt(full.substring(4, 6), 16),
  ];
}

/**
 * Accept the three input shapes every caller in the fleet already passes, plus
 * the `rgb(r, g, b)` strings this module's own constants are written in — so a
 * ratio can be taken against {@link CONTRAST_DARK_TEXT} without a conversion at
 * the call site.
 */
function toChannels(color) {
  if (typeof color === 'string') {
    const trimmed = color.trim();
    const rgb = trimmed.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i);
    if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
    return parseChannels(trimmed);
  }
  if (color && typeof color === 'object') {
    if (color.hex) return toChannels(color.hex);
    if (color.lightColor) return toChannels(color.lightColor);
  }
  throw new Error(`Unsupported color value: ${typeof color}`);
}

function round(channels) {
  return channels.map((value) => Math.round(Math.min(255, Math.max(0, value))));
}

function channelsToHex(channels) {
  return `#${channels
    .map((value) => Math.round(Math.min(255, Math.max(0, value))).toString(16).padStart(2, '0'))
    .join('')}`.toUpperCase();
}

function luminanceFromChannels([red, green, blue]) {
  const [r, g, b] = [red, green, blue]
    .map((value) => value / 255)
    .map((value) => (value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Parse any colour shape this module accepts into 0-255 channels.
 *
 * Exported because the brand module has to reason about HUE, not just contrast,
 * and it must accept the same shapes: checkout's palette arrives off the DONATE
 * postMessage wire and is NOT guaranteed to be generator-shaped, so its steps
 * can be `rgb()` strings rather than hex. A hue test that only understood hex
 * silently classified every `rgb()` step as having no hue at all.
 *
 * @param {string|{hex?: string, lightColor?: string}} color
 * @returns {[number, number, number]}
 * @throws if the value cannot be parsed.
 */
export function colorChannels(color) {
  return toChannels(color);
}

/**
 * WCAG 2.1 relative luminance.
 *
 * This is the formula the old heuristic looked like it was using and was not:
 * the same three coefficients, but applied to gamma-EXPANDED channels rather
 * than to raw 0–255 bytes, which is the whole difference between a perceptual
 * measure and a byte average.
 *
 * @param {string|{hex?: string, lightColor?: string}} color
 * @returns {number} 0 (black) … 1 (white).
 * @throws if the value cannot be parsed — the total functions below catch.
 */
export function relativeLuminance(color) {
  return luminanceFromChannels(toChannels(color));
}

/**
 * WCAG 2.1 contrast ratio between two colours. Symmetric; 1 … 21.
 *
 * @param {string|{hex?: string, lightColor?: string}} foreground
 * @param {string|{hex?: string, lightColor?: string}} background
 * @returns {number}
 * @throws if either value cannot be parsed.
 */
export function contrastRatio(foreground, background) {
  const [lighter, darker] = [relativeLuminance(foreground), relativeLuminance(background)].sort(
    (a, b) => b - a,
  );
  return (lighter + 0.05) / (darker + 0.05);
}

function normalizeColorInput(color, fnName) {
  // Early return for falsy values
  if (!color) return null;

  let colorString;
  if (typeof color === 'string') {
    colorString = color.trim();
  } else if (typeof color === 'object' && color?.hex) {
    colorString = color.hex;
  } else if (typeof color === 'object' && color?.lightColor) {
    colorString = color.lightColor;
  } else {
    console.warn(`${fnName}: Unsupported color type:`, typeof color, color);
    return null;
  }

  if (!colorString || colorString === 'null' || colorString === 'undefined') {
    console.warn(`${fnName}: Invalid color string:`, colorString);
    return null;
  }
  return colorString;
}

/**
 * Pick a readable ink for a brand background.
 *
 * Tries the two house inks first and takes the one with the higher real ratio
 * — which alone fixes the three shipped hues where the old heuristic chose the
 * worse one. If neither house ink reaches {@link CONTRAST_AA_NORMAL}, escalates
 * to the extreme of the same direction, which the module docstring proves can
 * never itself fall short. Ties go to the dark ink, matching the email
 * deriver's preference for ink over white at equal contrast.
 *
 * Never throws: an unparseable value returns {@link CONTRAST_ERROR_FALLBACK},
 * exactly as before.
 *
 * @param {string|{hex?: string, lightColor?: string}} color Brand colour.
 * @returns {string} A CSS colour string.
 */
export function contrastForeground(color) {
  const colorString = normalizeColorInput(color, 'contrastForeground');
  if (colorString === null) return CONTRAST_ERROR_FALLBACK;

  try {
    const background = toChannels(colorString);
    const luminance = luminanceFromChannels(background);
    const ratioTo = (ink) => {
      const [lighter, darker] = [relativeLuminance(ink), luminance].sort((a, b) => b - a);
      return (lighter + 0.05) / (darker + 0.05);
    };

    const dark = ratioTo(CONTRAST_DARK_TEXT);
    const light = ratioTo(CONTRAST_LIGHT_TEXT);
    if (Math.max(dark, light) >= CONTRAST_AA_NORMAL) {
      return dark >= light ? CONTRAST_DARK_TEXT : CONTRAST_LIGHT_TEXT;
    }

    // Neither house ink clears the floor, so escalate — and re-decide the
    // direction on the extremes' OWN ratios rather than inheriting the house
    // inks'. The two crossovers do not coincide: the house pair swaps over at
    // L = 0.22918 and black/white swap at L = 0.17913, so on the band between
    // them the darker house ink wins while WHITE is the better extreme.
    // Inheriting the direction leaves that band under the floor (measured: 1.6M
    // colours, worst 3.74:1) — see the CON-10 record's self-review.
    const black = ratioTo(CONTRAST_DARK_TEXT_MAX);
    const white = ratioTo(CONTRAST_LIGHT_TEXT_MAX);
    return black >= white ? CONTRAST_DARK_TEXT_MAX : CONTRAST_LIGHT_TEXT_MAX;
  } catch (error) {
    console.warn('contrastForeground: Color conversion failed:', colorString, error);
    return CONTRAST_ERROR_FALLBACK;
  }
}

/**
 * Pick the label for a BRAND-COLOURED FILL — a call-to-action button, a
 * selected payment card, a branded banner.
 *
 * {@link contrastForeground} picks whichever ink has the higher ratio, which on
 * a mid-tone brand is the dark one: on ICNA Relief's #00A950 it gives black at
 * 6.79:1 over white at 3.09:1. That is right for running text, but on a button
 * it reads as "the label is black whatever colour the nonprofit chose", which
 * nonprofits reported as a bug — before CON-10 the same fills carried a white
 * label. So a fill prefers the LIGHT ink whenever it clears
 * {@link CONTRAST_AA_LARGE}, the WCAG floor for large text and user-interface
 * components, and otherwise falls back to {@link contrastForeground}, so a pale
 * brand (a yellow, a light sage) still gets dark ink at the full AA floor.
 *
 * The house light ink is tried before pure white, for the same reason
 * contrastForeground tries the house inks first: a hue that never needed the
 * extreme does not get it.
 *
 * Never throws: an unparseable value returns {@link CONTRAST_ERROR_FALLBACK}.
 *
 * @param {string|{hex?: string, lightColor?: string}} color The fill colour.
 * @returns {string} A CSS colour string.
 */
export function brandFillForeground(color) {
  const colorString = normalizeColorInput(color, 'brandFillForeground');
  if (colorString === null) return CONTRAST_ERROR_FALLBACK;

  try {
    if (contrastRatio(CONTRAST_LIGHT_TEXT, colorString) >= CONTRAST_AA_LARGE) {
      return CONTRAST_LIGHT_TEXT;
    }
    if (contrastRatio(CONTRAST_LIGHT_TEXT_MAX, colorString) >= CONTRAST_AA_LARGE) {
      return CONTRAST_LIGHT_TEXT_MAX;
    }
    return contrastForeground(colorString);
  } catch (error) {
    console.warn('brandFillForeground: Color conversion failed:', colorString, error);
    return CONTRAST_ERROR_FALLBACK;
  }
}

/**
 * Make a brand step usable as TEXT on a neutral surface.
 *
 * `design.md` §1.5 forbids the call-to-action fill step from ever painting text
 * and gives that job to the ramp's contrasting step instead; §6.10 measured
 * that the contrasting step is itself marginally under the floor at two of the
 * four reviewed hues, so it needs a guard rather than a swap. This is that
 * guard, and it takes the email deriver's `accessibleForeground` SHAPE — the
 * same step size, the same iteration cap — walking the brand step toward a
 * house ink until it clears. Same shape, different ink and different starting
 * step, so the two do not return the same hex; see the note above.
 *
 * Returns a hex so the result is still recognisably the nonprofit's hue: the
 * point is brand-coloured text that can be read, not ink that has stopped being
 * branded. The walk moves away from the GROUND — toward the dark house ink on a
 * light surface, toward the light one on a dark surface.
 *
 * When it cannot reach the floor it gives up the hue and returns the
 * highest-contrast ink available against that ground. On a mid-luminance ground
 * that is the only correct answer: no brand-coloured text clears 4.50:1 on it,
 * so a function that kept trying to be brand-faithful there would just be
 * returning an unreadable value. **A `minRatio` above what the ground can carry
 * is therefore best-effort, not a promise** — it returns the maximum, which for
 * the default AA floor always clears (the proof above gives 4.5826:1 as the
 * minimum of that maximum) but for a AAA request on a mid-tone ground cannot.
 * No ink can: the maximum contrast against luminance 0.179 is 4.5826:1 for ANY
 * colour, so 7:1 there is unreachable rather than unimplemented.
 *
 * @param {string|{hex?: string, lightColor?: string}} color The brand step.
 * @param {object} [options]
 * @param {string} [options.background] Surface it sits on. Defaults to the
 *   lightest donor surface, the case every current call site is.
 * @param {number} [options.minRatio] Floor to clear. Best-effort above what
 *   the ground can carry — see above.
 * @returns {string} A CSS colour string.
 */
export function brandTextForeground(color, options = {}) {
  const { background = CONTRAST_LIGHT_TEXT_MAX, minRatio = CONTRAST_AA_NORMAL } = options;
  const colorString = normalizeColorInput(color, 'brandTextForeground');
  if (colorString === null) return CONTRAST_DARK_TEXT;

  try {
    const backgroundLuminance = relativeLuminance(background);
    // Walk AWAY from the ground, not always downward. Every shipped call site
    // passes a near-white ground, so walking toward the dark ink is right there
    // — but the option is exported API, and on a dark ground the same walk
    // moves the text TOWARD the background: it returned the dark ink at 1.00:1
    // for a dark brand on a dark ground, worse than leaving the value alone.
    const towardDark = backgroundLuminance > 0.1833;
    const target = toChannels(towardDark ? CONTRAST_DARK_TEXT : CONTRAST_LIGHT_TEXT);
    // When the walk cannot reach the floor, return the HIGHEST-contrast ink
    // available against this ground — the better of the two extremes — rather
    // than the ink we were walking toward, or an ink that merely clears AA.
    //
    // Two separate reasons, both measured. The house inks only clear 4.50:1
    // against grounds outside luminance 0.161-0.324, so returning one was a
    // floor that did not hold (2.82:1 for a mid brand on a mid grey). And a
    // caller may ask for a HIGHER floor than AA: deferring to a picker that
    // guarantees only AA would silently ignore that request. The maximum is the
    // strongest thing that can honestly be returned, and for the default floor
    // it is always at least 4.5826:1 — see the module docstring's proof.
    const lastResort = [CONTRAST_DARK_TEXT_MAX, CONTRAST_LIGHT_TEXT_MAX].reduce((best, ink) =>
      contrastRatio(ink, background) > contrastRatio(best, background) ? ink : best,
    );
    const clears = (channels) => {
      const [lighter, darker] = [luminanceFromChannels(channels), backgroundLuminance].sort(
        (a, b) => b - a,
      );
      return (lighter + 0.05) / (darker + 0.05) >= minRatio;
    };

    // `clears` is evaluated on the ROUNDED channels, because rounding is what
    // the emitted hex will carry: testing the un-rounded interpolation let 3,948
    // of a 592,704-colour sweep round back down below the floor (worst 4.4716:1).
    let channels = round(toChannels(colorString));
    for (let step = 0; step < BRAND_TEXT_MAX_STEPS && !clears(channels); step += 1) {
      channels = round(
        channels.map(
          (value, index) => value * (1 - BRAND_TEXT_STEP) + target[index] * BRAND_TEXT_STEP,
        ),
      );
    }
    return clears(channels) ? channelsToHex(channels) : lastResort;
  } catch (error) {
    console.warn('brandTextForeground: Color conversion failed:', colorString, error);
    return CONTRAST_DARK_TEXT;
  }
}
